/**
 * Client-side guardrails service — mirrors backend pattern checks.
 * Used to validate file content before sending to the server.
 */

export interface GuardrailResult {
  passed: boolean;
  category: string;
  message: string;
}

// ─── Pattern Definitions ──────────────────────────────────────────────────────

const PROMPT_INJECTION_PATTERNS: RegExp[] = [
  /ignore\s+(all\s+)?(previous|prior|above)\s+(instructions|prompts|rules)/i,
  /disregard\s+(all\s+)?(previous|prior|above)\s+(instructions|prompts|rules)/i,
  /forget\s+(all\s+)?(previous|prior|above)\s+(instructions|prompts|rules)/i,
  /you\s+are\s+now\s+(a|an)\s+\w+/i,
  /new\s+instructions?\s*:/i,
  /system\s*:\s*you\s+are/i,
  /<\|?(system|im_start|im_end)\|?>/i,
  /\[INST\]|\[\/INST\]/i,
  /###\s*(system|instruction|human|assistant)\s*:/i,
  /pretend\s+(you\s+are|to\s+be|that\s+you)/i,
  /act\s+as\s+if\s+(you\s+have\s+no|there\s+are\s+no)\s+(restrictions|rules|limits)/i,
  /override\s+(your|the|all)\s+(safety|content|system)/i,
  /reveal\s+(your|the)\s+(system\s+)?prompt/i,
  /what\s+(is|are)\s+your\s+(system\s+)?(prompt|instructions|rules)/i,
];

const JAILBREAK_PATTERNS: RegExp[] = [
  /\bDAN(?:\s+(?:mode|prompt))?\b/i,
  /do\s+anything\s+now/i,
  /developer\s+mode\s+(enabled|activated|on)/i,
  /jailbreak(ed)?/i,
  /bypass\s+(your|the|all)\s+(safety|content|ethical)\s*(filter|restriction|guardrail)/i,
  /in\s+(hypothetical|fictional|opposite)\s+(scenario|world|universe)/i,
  /evil\s+(twin|version|mode|alter\s*ego)/i,
  /no\s+(moral|ethical)\s+(constraints|boundaries|limits)/i,
  /unfiltered\s+(mode|response|output)/i,
  /respond\s+without\s+(any\s+)?(filter|restriction|limitation|censorship)/i,
  /(imagine|pretend|roleplay)\s+.{0,30}(no\s+rules|unrestricted|uncensored)/i,
];

const PII_PATTERNS: Record<string, RegExp> = {
  'SSN': /\b\d{3}-\d{2}-\d{4}\b/,
  'SSN (no dashes)': /\b(?!000|666|9\d{2})\d{3}(?!00)\d{2}(?!0000)\d{4}\b/,
  'Credit Card': /\b(?:\d{4}[-\s]?){3}\d{4}\b/,
  'Fax': /\bfax\s*(?:#|no\.?|number|num)?\s*[-:]?\s*(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/i,
  'Phone (US)': /\b(?:\+1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/,
  'Email': /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/,
  'URL': /https?:\/\/[^\s<>]+/i,
  'IP Address': /\b(?:\d{1,3}\.){3}\d{1,3}\b/,
  'Date of Birth': /\b(?:DOB|date\s{1,3}of\s{1,3}birth|birth\s{1,3}date|born\s{1,3}on)[ \t]*[-:]?[ \t]*(?:\d{1,2}[-/]\d{1,2}[-/]\d{2,4}|(?:19|20)\d{6})\b/i,
  'Related Date': /\b(?:admission|discharge|service|visit|encounter|appointment|procedure|death|claim)\s*date\s*[-:]?\s*(?:\d{1,2}[-/]\d{1,2}[-/]\d{2,4}|(?:19|20)\d{6})\b/i,
  'Age over 89': /\b(?:age\s*(?:is|:|=)?\s*(?:9[0-9]|1\d{2,})|(?:9[0-9]|1\d{2,})\s*(?:years old|y\/o))\b/i,
  'Member ID': /\bmember[ \t]*(?:id\b|#|number\b)[ \t]*[-:]?[ \t]*[A-Z0-9]{6,15}\b/i,
  'MRN': /\b(?:MRN|medical[ \t]*record)[ \t]*[-:#]?[ \t]*\d{5,12}\b/i,
  'Street Address': /\b\d{1,5}\s+(?:[NSEW]\.?\s+)?(?:[A-Z][a-z]+\s+){1,3}(?:St(?:reet)?|Ave(?:nue)?|Blvd|Boulevard|Dr(?:ive)?|Ln|Lane|Rd|Road|Ct|Court|Way|Pl(?:ace)?|Cir(?:cle)?|Pkwy|Parkway|Ter(?:race)?|Hwy|Highway)\b\.?/i,
  'City': /\bcity\s*[:=]\s*[A-Z][A-Za-z .'-]{1,50}\b/i,
  'County': /\bcounty\s*[:=]\s*[A-Z][A-Za-z .'-]{1,50}\b/i,
  'ZIP+4': /\b\d{5}-\d{4}\b/,
  'ZIP Code': /\b(?:zip|zipcode|zip\s*code|postal\s*code)\s*[:=#-]?\s*\d{5}(?:-\d{4})?\b/i,
  'Person Name': /\b(?:patient|member|subscriber|insured|beneficiary|name)\s*[:=]\s*[A-Z][a-z]{1,20}\s+[A-Z][a-z]{1,20}(?:\s+[A-Z][a-z]{1,20})?\b/,
  'Account Number': /\b(?:account|acct)\s*(?:#|no\.?|number|num)?\s*[-:]?\s*[A-Z0-9]{6,20}\b/i,
  'License Number': /\b(?:license|licence|DL|driver'?s?\s*lic(?:ense)?)\s*(?:#|no\.?|number|num)?\s*[-:]?\s*[A-Z0-9]{5,20}\b/i,
  'Certificate Number': /\b(?:certificate|cert)\s*(?:#|no\.?|number|num)?\s*[-:]?\s*[A-Z0-9]{5,20}\b/i,
  'VIN': /\bVIN\s*[-:#]?\s*[A-HJ-NPR-Z0-9]{17}\b/i,
  'Device ID': /\b(?:UDI|device\s*(?:id|identifier|serial))\s*[-:#]?\s*[A-Z0-9()]{8,30}\b/i,
  'Biometric Identifier': /\b(?:fingerprint|voiceprint|retina|iris\s*scan|palm\s*print|biometric)\s*(?:id|identifier|hash|template|scan|print)?\s*[-:#]?\s*[A-Z0-9+/_-]{6,}\b/i,
  'Face Image': /\b(?:full[-\s]?face\s+(?:photo|image)|face\s+(?:photo|image)|(?:patient|member|subscriber)\s+(?:photo|image|portrait|headshot))\b/i,
  'Embedded Image': /data:image\/[a-z]+;base64,[A-Za-z0-9+/=]{100,}/,
  'Other Unique ID': /\b(?:unique\s*(?:id|identifier|code)|serial\s*(?:number|no\.?|#)?|tracking\s*(?:id|number)|case\s*(?:id|number)|authorization\s*(?:id|number)|auth\s*(?:id|number))\s*[-:#]?\s*[A-Z0-9-]{6,30}\b/i,
};

const TOXIC_PATTERNS: RegExp[] = [
  /\b(kill|murder|harm|attack|assault|rape)\s+(yourself|himself|herself|them|people|someone)\b/i,
  /\b(hate|despise)\s+(all\s+)?(women|men|blacks|whites|jews|muslims|christians|gays|trans)\b/i,
  /\bhow\s+to\s+(make|build|create)\s+(a\s+)?(bomb|weapon|explosive|poison|drug)\b/i,
  /\b(suicide|self[- ]harm)\s+(method|instruction|how\s+to)\b/i,
];

const CODE_INJECTION_PATTERNS: RegExp[] = [
  /;\s*(DROP|DELETE|ALTER|TRUNCATE|INSERT|UPDATE)\s+/i,
  /'\s+(?:OR|AND)\s+/i,
  /(UNION\s+(ALL\s+)?SELECT)/i,
  /<script[\s>]/i,
  /javascript\s*:/i,
  /on(error|load|click|mouseover)\s*=/i,
  /\beval\s*\(/i,
  /\bexec\s*\(/i,
  /__import__\s*\(/i,
  /subprocess\.(run|call|Popen)\s*\(/i,
  /os\.(system|popen|exec)\s*\(/i,
  /\brm\s+-rf\b/i,
  /&&\s*(rm|del|format|shutdown|mkfs)\b/i,
];

const OFF_TOPIC_PATTERNS: RegExp[] = [
  /\bhow\s+to\s+(hack|crack|pirate|steal)\b/i,
  /\b(generate|create|write)\s+(malware|virus|ransomware|exploit)\b/i,
  /\b(illegal|illicit)\s+(drug|weapon|activity)\s+(recipe|guide|instruction)\b/i,
];

// ─── Check Functions ──────────────────────────────────────────────────────────

function pass(category: string): GuardrailResult {
  return { passed: true, category, message: '' };
}

function fail(category: string, message: string): GuardrailResult {
  return { passed: false, category, message };
}

export function checkPromptInjection(text: string): GuardrailResult {
  for (const p of PROMPT_INJECTION_PATTERNS) {
    if (p.test(text)) return fail('PROMPT_INJECTION', 'Input contains prompt injection attempt.');
  }
  return pass('PROMPT_INJECTION');
}

export function checkJailbreak(text: string): GuardrailResult {
  for (const p of JAILBREAK_PATTERNS) {
    if (p.test(text)) return fail('JAILBREAK', 'Input contains jailbreaking attempt.');
  }
  return pass('JAILBREAK');
}

export function checkPII(text: string): GuardrailResult {
  for (const [piiType, pattern] of Object.entries(PII_PATTERNS)) {
    if (pattern.test(text)) {
      return fail('PII', `Input contains potential ${piiType}. Please remove personal data before submitting.`);
    }
  }
  return pass('PII');
}

export function checkToxicContent(text: string): GuardrailResult {
  for (const p of TOXIC_PATTERNS) {
    if (p.test(text)) return fail('TOXIC_CONTENT', 'Input contains harmful or toxic content.');
  }
  return pass('TOXIC_CONTENT');
}

export function checkCodeInjection(text: string): GuardrailResult {
  for (const p of CODE_INJECTION_PATTERNS) {
    if (p.test(text)) return fail('CODE_INJECTION', 'Input contains potential code or SQL injection.');
  }
  return pass('CODE_INJECTION');
}

export function checkTopical(text: string): GuardrailResult {
  for (const p of OFF_TOPIC_PATTERNS) {
    if (p.test(text)) return fail('TOPICAL', 'Request is outside permitted topics.');
  }
  return pass('TOPICAL');
}

/**
 * Run all input guardrails. Returns the first failure or a pass.
 * PII is NOT blocked here — it is handled by redactPHI() at the LLM boundary.
 */
export function validateInput(text: string): GuardrailResult {
  const checks = [checkPromptInjection, checkJailbreak, checkToxicContent, checkCodeInjection, checkTopical];
  for (const check of checks) {
    const r = check(text);
    if (!r.passed) return r;
  }
  return { passed: true, category: 'ALL', message: 'All guardrails passed.' };
}

/**
 * Security-only checks (no PII/toxic) — for healthcare data that legitimately
 * contains phone numbers, member IDs, SSNs, etc.
 */
function validateSecurity(text: string): GuardrailResult {
  const checks = [checkPromptInjection, checkJailbreak, checkCodeInjection, checkTopical];
  for (const check of checks) {
    const r = check(text);
    if (!r.passed) return r;
  }
  return { passed: true, category: 'ALL', message: 'All security checks passed.' };
}

/**
 * Validate file content — security checks only (no PII).
 * Healthcare data files legitimately contain phone numbers, member IDs, SSNs, etc.
 * @param content Full file text content
 */
export function validateFileContent(content: string): GuardrailResult {
  const sample = content.slice(0, 50000);
  return validateSecurity(sample);
}

/**
 * Validate individual field entries (names + examples) — security checks only.
 * Healthcare fields like "Payer Support Telephone Number" with phone values are expected.
 */
export function validateFields(fields: { name: string; example: string }[]): GuardrailResult {
  for (const field of fields) {
    const r = validateSecurity(`${field.name} ${field.example}`);
    if (!r.passed) {
      return { ...r, message: `${r.message} (field: "${field.name}")` };
    }
  }
  return { passed: true, category: 'ALL', message: 'All fields passed guardrail checks.' };
}
